import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// NOTE: External CSS import removed. Styles are injected via the <style> tag below.

/** =========================
 * PROD CONFIG
 * ========================= */
const CLIENT_ID = "3X8KJ5OiCVAe71GQtTS8FAI3gNjDMFiE";
const REDIRECT_URI = "https://admin.lianfoong.com/add-customer";
const BACKEND_CALLBACK = "https://www.lianfoong.com/auth/singpass/web/callback";
const OCR_ENDPOINT = "https://www.lianfoong.com/api/ocr/nric";
const CUSTOMERS_ENDPOINT = "https://www.lianfoong.com/api/customers";
const UPDATE_TXN_ENDPOINT = "https://www.lianfoong.com/api/pawn-transactions/update";
const CREATE_TXN_ENDPOINT = "https://www.lianfoong.com/api/pawn-transactions";

const MAX_MB = 8;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

/** PKCE utilities */
const rand = (len) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_.~";
  return Array.from({ length: len }, () => chars[(Math.random() * chars.length) | 0]).join("");
};
async function sha256ToB64Url(str) {
  const enc = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest("SHA-256", enc);
  const bytes = new Uint8Array(digest);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Small helpers */
const getQuery = () => new URLSearchParams(window.location.search);
const toTitle = (s) => (s || "").toString().toUpperCase();

/** Basic validations */
const NRIC_RE = /^[STFGM]\d{7}[A-Z]$/i;

export default function PawnerIntake() {
  const navigate = useNavigate();
  const location = useLocation();
  const { transactionData } = location.state || {};

  /** ---------- Form state ---------- */
  const [nric, setNric] = useState("");
  const [name, setName] = useState("");
  const [race, setRace] = useState("");
  const [sex, setSex] = useState("");
  const [nationality, setNationality] = useState("");
  const [sgAddr, setSgAddr] = useState("");
  const [dob, setDob] = useState(""); // YYYY-MM-DD
  const [mobile, setMobile] = useState(""); 
  const [blacklisted, setBlacklisted] = useState(false);

  /** ---------- OCR files & camera ---------- */
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState("");
  const [backPreview, setBackPreview] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [fileError, setFileError] = useState("");

  // Live camera
  const [camOpen, setCamOpen] = useState(false);
  const [camSide, setCamSide] = useState("user");
  const [camFor, setCamFor] = useState("front");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  /** ---------- Helpers & Validation ---------- */

  // Handle Escape key to go back or close camera
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (camOpen) {
          // If camera is open, close it
          stopCamera();
          setCamOpen(false);
        } else {
          // Otherwise, navigate back to dashboard
          navigate("/dashboard");
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, camOpen]);

  // ~~~ Image compression helpers ~~~
  async function compressImageFile(
    file,
    {
      maxWidth = 1600,        // cap long edge (keeps quality good)
      maxHeight = 1600,
      quality = 0.82,         // JPEG quality (0–1)
      mimeType = "image/jpeg" // force JPEG to keep size small
    } = {}
  ) {
    if (!file || !file.type.startsWith("image/")) return file;

    // Create an object URL so <img> can load the file
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = url;
      });

      // Compute scale while preserving aspect ratio
      let { width: w, height: h } = img;
      const scale = Math.min(1, maxWidth / w, maxHeight / h);
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);

      // Draw to canvas (this strips metadata & EXIF, reducing size)
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, cw, ch);

      // Encode to JPEG
      const blob = await new Promise((res) =>
        canvas.toBlob(res, mimeType, quality)
      );
      if (!blob) return file;

      // If the "compressed" file somehow ended up larger, keep original
      if (blob.size >= file.size) return file;

      const name = (file.name || "image").replace(/\.\w+$/, "") + ".jpg";
      return new File([blob], name, { type: mimeType, lastModified: Date.now() });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  const TARGET_MAX_BYTES = 2.5 * 1024 * 1024; // ~2.5MB per image to stay well under server/base64 overhead

  async function downscaleIfNeeded(file) {
    if (!file) return file;
    // Always convert PNG/HEIC/WebP to JPEG (smaller after canvas) or if too large
    const needsCompress =
      file.size > TARGET_MAX_BYTES ||
      /image\/(png|heic|heif|webp)/i.test(file.type);

    return needsCompress
      ? await compressImageFile(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.8 })
      : file;
  }

  const validateAndSet = async (file, isFront) => {
    setFileError("");

    const setState = isFront
      ? (f, p) => {
          if (frontPreview) URL.revokeObjectURL(frontPreview);
          setFrontFile(f);
          setFrontPreview(p || "");
        }
      : (f, p) => {
          if (backPreview) URL.revokeObjectURL(backPreview);
          setBackFile(f);
          setBackPreview(p || "");
        };

    if (!file) return setState(null, "");

    // Try to shrink big images (esp. iPad camera) before any size checks
    let processed = await downscaleIfNeeded(file);

    // Optional: keep your existing type guard, but allow HEIC/HEIF too
    const okType = /image\/(jpeg|jpg|png|webp|heic|heif)/i.test(processed.type || file.type || "");
    if (!okType) {
      setFileError("Only JPG/PNG/WEBP/HEIC are supported.");
      return;
    }

    if (processed.size > MAX_MB * 1024 * 1024) {
      setFileError(`Image still too large after compression. Please retake (under ${MAX_MB}MB).`);
      return;
    }

    const previewUrl = URL.createObjectURL(processed);
    setState(processed, previewUrl);
  };

  /** ---------- OCR ---------- */
  const handleScan = async () => {
    if (!frontFile || !backFile) return alert("Please provide both front & back NRIC images.");
    try {
      setScanLoading(true);
      const fd = new FormData();
      fd.append("front", frontFile);
      fd.append("back", backFile);
      const { data } = await axios.post(OCR_ENDPOINT, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.nric) setNric(toTitle(data.nric));
      if (data.name) setName(toTitle(data.name));
      if (data.address) setSgAddr(toTitle(data.address));
      if (data.race) setRace(toTitle(data.race));
      if (data.sex) setSex(toTitle(data.sex));
      if (data.nationality) setNationality(toTitle(data.nationality));

      const dobStr = (data.dob || "").trim();
      if (dobStr) {
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(dobStr)
          ? dobStr
          : new Date(dobStr).toISOString().slice(0, 10);
        setDob(iso);
      }
    } catch (e) {
      console.error(e);
      alert("Scan failed. Please try again.");
    } finally {
      setScanLoading(false);
    }
  };

  /** ---------- Live camera controls ---------- */
  const openCamera = async (side = "user", forWhich = "front") => {
    try {
      setCamSide(side);
      setCamFor(forWhich);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: side } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamOpen(true);
    } catch {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCamOpen(true);
      } catch (err) {
        alert("Unable to access camera. Ensure HTTPS and permissions are granted.");
        console.error(err);
      }
    }
  };

  const takeSnapshot = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Cap the long edge to 1600px to keep files small
    let w = video.videoWidth;
    let h = video.videoHeight;
    const MAX_W = 1600;
    const scale = w > h ? Math.min(1, MAX_W / w) : Math.min(1, MAX_W / h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", 0.8)
    );
    if (!blob) return;

    // Wrap as File and pass through the same compression path (no-op if already small)
    const raw = new File([blob], `nric-${camFor}.jpg`, { type: "image/jpeg" });
    const processed = await downscaleIfNeeded(raw);

    await validateAndSet(processed, camFor === "front");
    stopCamera();
    setCamOpen(false);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopCamera(), []);

  /** ---------- Singpass (PKCE) ---------- */
  const startSingpass = async () => {
    const state = rand(32);
    const nonce = rand(32);
    const verifier = rand(64);
    const challenge = await sha256ToB64Url(verifier);

    sessionStorage.setItem("sp_state", state);
    sessionStorage.setItem("sp_nonce", nonce);
    sessionStorage.setItem("sp_verifier", verifier);

    const scope = encodeURIComponent("openid uinfin name dob mobileno nationality race sex regadd");
    const authUrl =
      `https://id.singpass.gov.sg/auth?client_id=${encodeURIComponent(CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
      `&response_type=code&scope=${scope}` +
      `&state=${encodeURIComponent(state)}&nonce=${encodeURIComponent(nonce)}` +
      `&code_challenge_method=S256&code_challenge=${challenge}`;

    window.location.assign(authUrl);
  };

  // On return from Singpass
  useEffect(() => {
    const qs = getQuery();
    const code = qs.get("code");
    const retState = qs.get("state");
    if (!code || !retState) return;

    const expectedState = sessionStorage.getItem("sp_state");
    const verifier = sessionStorage.getItem("sp_verifier");
    if (!verifier || !expectedState || expectedState !== retState) {
      console.warn("State/verifier mismatch.");
      return;
    }

    (async () => {
      try {
        const res = await fetch(BACKEND_CALLBACK, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, state: retState, codeVerifier: verifier }),
        });
        if (!res.ok) throw new Error(await res.text());
        const prof = await res.json();

        if (prof.nric) setNric(toTitle(prof.nric));
        if (prof.name) setName(toTitle(prof.name));
        if (prof.dob)
          setDob(/^\d{4}-\d{2}-\d{2}$/.test(prof.dob) ? prof.dob : new Date(prof.dob).toISOString().slice(0, 10));
        if (prof.mobileno) setMobile(String(prof.mobileno));
        if (prof.nationality) setNationality(toTitle(prof.nationality));
        if (prof.race) setRace(toTitle(prof.race));
        if (prof.sex) setSex(toTitle(prof.sex));
        if (prof.address) setSgAddr(toTitle(prof.address));

        // Clean URL (remove ?code=…)
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        alert("Singpass verification failed.");
        console.error(e);
      }
    })();
  }, []);

  /** Reset form */
  const resetForm = () => {
    setNric(""); setName(""); setRace(""); setSex(""); setNationality("");
    setSgAddr(""); setDob(""); setMobile("");
    setBlacklisted(false);
    if (frontPreview) URL.revokeObjectURL(frontPreview);
    if (backPreview) URL.revokeObjectURL(backPreview);
    setFrontFile(null); setBackFile(null); setFrontPreview(""); setBackPreview("");
    stopCamera(); setCamOpen(false);
  };

  /** ---------- Required-field validation ---------- */
  const validateRequired = () => {
    const missing = [];
    const pushIfEmpty = (val, label) => { if (!val || String(val).trim() === "") missing.push(label); };
    pushIfEmpty(nric, "NRIC No");
    pushIfEmpty(name, "Name");
    pushIfEmpty(race, "Race");
    pushIfEmpty(sex, "Sex");
    pushIfEmpty(nationality, "Nationality");
    pushIfEmpty(sgAddr, "Address (Singapore)");
    pushIfEmpty(dob, "Birthdate");

    if (missing.length) {
      alert("Please fill the following required fields:\n• " + missing.join("\n• "));
      return false;
    }
    if (!NRIC_RE.test(nric)) {
      if (!window.confirm("NRIC format looks off. Submit anyway?")) return false;
    }
    return true;
  };

  /** ---------- Submit ---------- */
  const handleSubmit = async () => {
    if (!validateRequired()) return;

    if (!mobile || !mobile.trim()) {
      const ok = window.confirm("Mobile number is empty. Proceed without it?");
      if (!ok) return;
    }

    try {
      // 1) Upsert customer
      await axios.post(CUSTOMERS_ENDPOINT, {
        nirc: nric,
        name,
        race,
        sex,
        nationality,
        sgAddr,
        dob,
        contactNoMobile: mobile || null,
        blacklisted,
      });

      // 2) Update OR create transaction
      if (transactionData?.receiptNo) {
        await axios.put(
          `${UPDATE_TXN_ENDPOINT}?receiptNo=${transactionData.receiptNo}`,
          { ...transactionData, pawnerNirc: nric }
        );
      } else if (transactionData) {
        const resp = await axios.post(
          CREATE_TXN_ENDPOINT,
          {
            ...transactionData,
            pawnerNirc: nric,
            amount: Number(transactionData.principal),
            valuationAmount: Number(transactionData.valuation),
            item: transactionData.items,
            weight: transactionData.weight,
            remark: transactionData.remark,
            noOfItem: transactionData.noOfItem,
            grantDate: transactionData.grantDate,
            expiryDate: transactionData.expiryDate,
            activeRecord: true,
          },
          { responseType: "blob" }
        );

        const blob = new Blob([resp.data], { type: "application/pdf" });
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "receipt.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }

      alert("Saved successfully.");
      resetForm();
    } catch (e) {
      console.error(e);
      alert("Save failed.");
    }
  };

  return (
    <>
      <style>{`
        /* Reset & Container */
        .pawner-form-container {
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          /* Increased top padding significantly (40rem / ~640px) to ensure visibility under large fixed headers */
          padding: 40rem 1.5rem 3rem;
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #333;
        }

        .form-title {
          text-align: center;
          margin-bottom: 2rem;
          font-size: 1.75rem;
          color: #2c3e50;
        }

        /* Fluid Grid System */
        .form-row {
          display: flex;
          flex-wrap: wrap; /* Critical for zoom adaptation */
          gap: 1rem;
          margin-bottom: 1.25rem;
        }

        .form-group {
          flex: 1 1 300px; /* Flex-grow, Flex-shrink, Basis. Allows wrapping on zoom/mobile */
          display: flex;
          flex-direction: column;
          min-width: 0; /* Prevents flex overflow */
        }

        /* Labels & Inputs */
        label {
          margin-bottom: 0.5rem;
          font-weight: 600;
          font-size: 0.95rem;
          color: #555;
        }

        .input-field {
          width: 100%;
          padding: 0.75rem; /* Large touch target for mobile */
          font-size: 1rem; /* 16px font prevents iOS zoom-on-focus */
          border: 1px solid #ccc;
          border-radius: 6px;
          box-sizing: border-box; /* Ensures padding doesn't break width */
          transition: border-color 0.2s;
        }

        textarea.input-field {
          resize: vertical;
          min-height: 80px;
        }

        .input-field:focus {
          border-color: #007bff;
          outline: none;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        /* Checkbox Styling */
        .checkbox-group {
          flex-direction: row;
          align-items: center;
          gap: 0.75rem;
          margin: 1.5rem 0;
        }
        
        input[type="checkbox"] {
          width: 1.25rem;
          height: 1.25rem;
          cursor: pointer;
        }

        /* Button Styling */
        .button-group {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        button {
          padding: 0.85rem 1.5rem;
          font-size: 1rem;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s;
          flex: 1 1 auto; /* Buttons fill width on small screens */
          font-weight: 600;
          text-align: center;
          display: inline-flex;
          justify-content: center;
          align-items: center;
        }

        button:active {
          transform: translateY(1px);
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .ok-button {
          background-color: #007bff;
          color: white;
        }

        .cancel-button {
          background-color: #6c757d;
          color: white;
        }

        /* File Upload & Camera Areas */
        input[type="file"] {
          padding: 0.5rem 0;
          font-size: 0.9rem;
          width: 100%;
        }

        .camera-controls {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
          flex-wrap: wrap;
        }

        .camera-controls button {
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
        }

        /* Images and Video */
        img, video {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          border: 1px solid #ddd;
          background: #f8f9fa;
        }

        /* Camera Modal Overlay */
        .camera-modal {
          border: 1px solid #ddd;
          background: #fff;
          border-radius: 12px;
          padding: 1rem;
          margin: 1rem 0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        /* Error Messages */
        .error-text {
          color: #dc3545;
          font-size: 0.9rem;
          margin-top: 0.5rem;
          font-weight: 500;
        }
      `}</style>

      <div className="pawner-form-container">
        <h2 className="form-title">Pawner Record</h2>

        {/* Singpass quick action */}
        <div className="button-group" style={{ marginBottom: '1.5rem' }}>
          <button className="ok-button" onClick={startSingpass}>Fetch via Singpass</button>
        </div>

        {/* NRIC image capture / upload */}
        <div className="form-row">
          <div className="form-group">
            <label>NRIC Front:</label>
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => validateAndSet(e.target.files?.[0] ?? null, true)}
            />
            {frontPreview && <img src={frontPreview} alt="front" style={{ marginTop: 6, maxWidth: 180, maxHeight: 120, borderRadius: 6 }} />}
            <div className="camera-controls">
              <button type="button" className="ok-button" onClick={() => openCamera("user", "front")}>Front Cam</button>
              <button type="button" className="ok-button" onClick={() => openCamera("environment", "front")}>Rear Cam</button>
            </div>
          </div>
          <div className="form-group">
            <label>NRIC Back:</label>
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => validateAndSet(e.target.files?.[0] ?? null, false)}
            />
            {backPreview && <img src={backPreview} alt="back" style={{ marginTop: 6, maxWidth: 180, maxHeight: 120, borderRadius: 6 }} />}
            <div className="camera-controls">
              <button type="button" className="ok-button" onClick={() => openCamera("user", "back")}>Front Cam</button>
              <button type="button" className="ok-button" onClick={() => openCamera("environment", "back")}>Rear Cam</button>
            </div>
          </div>
        </div>

        {fileError && <div className="error-text">{fileError}</div>}

        <div className="form-group">
          <button type="button" className="ok-button" onClick={handleScan} disabled={scanLoading || !frontFile || !backFile}>
            {scanLoading ? "Scanning…" : "Scan NRIC (OCR)"}
          </button>
        </div>

        {/* Live camera modal-ish */}
        {camOpen && (
          <div className="camera-modal">
            <p style={{ marginBottom: 6 }}>
              Camera ({camSide === "user" ? "Front" : "Rear"}) — capturing for <b>{camFor}</b> side
            </p>
            <video ref={videoRef} playsInline muted />
            <div className="button-group" style={{ marginTop: 8 }}>
              <button className="ok-button" onClick={takeSnapshot}>Capture</button>
              <button className="cancel-button" onClick={() => { stopCamera(); setCamOpen(false); }}>Close</button>
            </div>
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
        )}

        {/* Fields */}
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>NRIC No:</label>
          <input className="input-field" value={nric} onChange={(e) => setNric(toTitle(e.target.value))} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Race:</label>
            <input className="input-field" value={race} onChange={(e) => setRace(toTitle(e.target.value))} />
          </div>
          <div className="form-group">
            <label>Sex:</label>
            <input className="input-field" value={sex} onChange={(e) => setSex(toTitle(e.target.value))} />
          </div>
        </div>

        <div className="form-group">
          <label>Name:</label>
          <input className="input-field" value={name} onChange={(e) => setName(toTitle(e.target.value))} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Nationality:</label>
            <input className="input-field" value={nationality} onChange={(e) => setNationality(toTitle(e.target.value))} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Address (Singapore):</label>
            <textarea className="input-field" value={sgAddr} onChange={(e) => setSgAddr(toTitle(e.target.value))} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Birthdate:</label>
            <input type="date" className="input-field" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Contact No (Mobile):</label>
            <input
              className="input-field"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Optional. Any format"
            />
          </div>
        </div>

        <div className="form-group checkbox-group">
          <input type="checkbox" id="blacklisted" checked={blacklisted} onChange={(e) => setBlacklisted(e.target.checked)} />
          <label htmlFor="blacklisted">Blacklisted</label>
        </div>

        <div className="button-group">
          <button className="cancel-button" onClick={() => navigate("/dashboard")}>Cancel</button>
          <button className="ok-button" onClick={handleSubmit}>Submit</button>
        </div>
      </div>
    </>
  );
}